import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Star, User, Plus, Upload, X, Loader2, CheckCircle, Car } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const REV_BG = "https://images.unsplash.com/photo-1727893512947-8bdc773ceb02?w=1920&q=80";

function StarRating({ value, onChange, readOnly = false, size = "w-6 h-6" }) {
    const [hover, setHover] = useState(0);
    return (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
                <button
                    key={s}
                    type="button"
                    disabled={readOnly}
                    onClick={() => onChange?.(s)}
                    onMouseEnter={() => !readOnly && setHover(s)}
                    onMouseLeave={() => !readOnly && setHover(0)}
                    className={readOnly ? "cursor-default" : "cursor-pointer transition-transform hover:scale-110"}
                    data-testid={`star-${s}`}
                >
                    <Star className={`${size} ${s <= (hover || value) ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`} />
                </button>
            ))}
        </div>
    );
}

export default function ReviewsPage() {
    const [reviews, setReviews] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [form, setForm] = useState({ author_name: "", text: "", rating: 5, author_photo: "", car_name: "" });
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const settings = useSettings();

    const load = useCallback(() => {
        api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const handlePhotoSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            alert("Файл слишком большой. Максимум 5 МБ.");
            return;
        }
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview("");
    };

    const handleSubmit = async () => {
        if (!form.author_name.trim() || !form.text.trim()) return;
        setSubmitting(true);
        try {
            let photoUrl = "";
            if (photoFile) {
                const fd = new FormData();
                fd.append("file", photoFile);
                const uploadRes = await api.post("/upload-public", fd);
                photoUrl = uploadRes.data.url;
            }
            await api.post("/reviews/submit", {
                author_name: form.author_name,
                text: form.text,
                rating: form.rating,
                author_photo: photoUrl,
                car_name: form.car_name,
            });
            setSuccess(true);
            setForm({ author_name: "", text: "", rating: 5, author_photo: "", car_name: "" });
            setPhotoFile(null);
            setPhotoPreview("");
            setTimeout(() => { setSuccess(false); setShowForm(false); }, 3000);
        } catch {
            alert("Ошибка при отправке. Попробуйте позже.");
        } finally {
            setSubmitting(false);
        }
    };

    const openForm = () => {
        setSuccess(false);
        setForm({ author_name: "", text: "", rating: 5, author_photo: "", car_name: "" });
        setPhotoFile(null);
        setPhotoPreview("");
        setShowForm(true);
    };

    return (
        <>
        <title>Отзывы | AutoTreider</title>
        <meta name="description" content={`${settings?.reviews_title || "Отзывы клиентов"} о покупке и доставке автомобилей из Европы.`} />
        <link rel="canonical" href="https://autotreider.eu/reviews" />
        <meta property="og:title" content={`${settings?.reviews_title || "Отзывы"} | ${settings?.company_name || "AutoTreider"}`} />
        <meta property="og:description" content={`${settings?.reviews_title || "Отзывы клиентов"} о покупке автомобилей из Европы.`} />
        <meta property="og:url" content="https://autotreider.eu/reviews" />
        <div className="min-h-screen pt-16 sm:pt-20" data-testid="reviews-page">
            <section className="relative h-[30vh] sm:h-[40vh] flex items-center">
                <div className="absolute inset-0"><img src={REV_BG} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/70" /></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full">
                    <p className="text-[#0099FF] text-xs uppercase tracking-[0.3em] mb-2 sm:mb-3 font-body">{settings?.reviews_subtitle || "Отзывы"}</p>
                    <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white font-display mb-4 sm:mb-0">{settings?.reviews_title || "Отзывы клиентов"}</h1>
                    <Button
                        onClick={openForm}
                        className="mt-3 sm:absolute sm:right-6 md:right-12 sm:bottom-0 bg-[#0099FF] text-white rounded-none uppercase tracking-wider text-xs px-5 py-3 hover:shadow-[0_0_20px_rgba(0,153,255,0.4)] transition-all"
                        data-testid="leave-review-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" /> Оставить отзыв
                    </Button>
                </div>
            </section>
            <section className="py-12 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                    {reviews.length === 0 ? (
                        <p className="text-zinc-500 text-center py-12 sm:py-20 font-body">Отзывов пока нет</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {reviews.map((r, i) => (
                                <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="bg-[#09090b] border border-white/5 p-5 sm:p-8" data-testid={`review-card-${r.id}`}>
                                    <div className="flex items-center gap-3 mb-3 sm:mb-4">
                                        {r.author_photo ? <img src={resolveImg(r.author_photo)} alt={r.author_name} loading="lazy" className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-full flex-shrink-0" /> : <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 flex items-center justify-center rounded-full flex-shrink-0"><User className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-500" /></div>}
                                        <div className="min-w-0">
                                            <p className="text-white font-semibold font-body text-sm sm:text-base truncate">{r.author_name}</p>
                                            {r.car_name && <p className="text-[#0099FF] text-xs font-body truncate">{r.car_name}</p>}
                                            <p className="text-zinc-500 text-xs font-body">{r.date}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 mb-3 sm:mb-4">{[...Array(5)].map((_, idx) => <Star key={idx} className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${idx < r.rating ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />)}</div>
                                    <p className="text-zinc-400 text-sm leading-relaxed font-body">{r.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="bg-[#09090b] border-white/10 w-[calc(100%-2rem)] max-w-md mx-auto max-h-[90vh] overflow-y-auto" data-testid="review-submit-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white font-display text-lg">Оставить отзыв</DialogTitle>
                    </DialogHeader>
                    {success ? (
                        <div className="py-6 sm:py-8 text-center" data-testid="review-success">
                            <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-emerald-400 mx-auto mb-3 sm:mb-4" />
                            <p className="text-white font-semibold text-base sm:text-lg mb-2 font-body">Спасибо за отзыв!</p>
                            <p className="text-zinc-400 text-xs sm:text-sm font-body">Он будет опубликован после проверки администратором.</p>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3 sm:space-y-4">
                                <div>
                                    <Label className="text-zinc-400 text-xs">Ваше имя *</Label>
                                    <Input value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Иван Иванов" className="bg-black/50 border-white/10 text-white h-10 rounded-none text-sm" data-testid="review-name-input" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-xs">Автомобиль</Label>
                                    <Input value={form.car_name} onChange={(e) => setForm({ ...form, car_name: e.target.value })} placeholder="BMW X5, Mercedes S-Class..." className="bg-black/50 border-white/10 text-white h-10 rounded-none text-sm" data-testid="review-car-input" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-xs">Оценка</Label>
                                    <StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-xs">Ваш отзыв *</Label>
                                    <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Расскажите о вашем опыте..." className="bg-black/50 border-white/10 text-white rounded-none text-sm" rows={3} data-testid="review-text-input" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-xs">Фото (макс. 5 МБ)</Label>
                                    {photoPreview ? (
                                        <div className="relative w-16 h-16 sm:w-20 sm:h-20 mt-2">
                                            <img src={photoPreview} alt="" className="w-full h-full object-cover rounded-full" />
                                            <button onClick={removePhoto} className="absolute -top-1 -right-1 w-5 h-5 bg-[#E31E24] text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                                        </div>
                                    ) : (
                                        <label className="mt-2 flex items-center gap-2 px-3 py-2 border border-dashed border-white/10 cursor-pointer hover:border-[#0099FF]/50 transition-colors" data-testid="review-photo-upload">
                                            <Upload className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                            <span className="text-zinc-500 text-xs sm:text-sm font-body">Загрузить фото</span>
                                            <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} />
                                        </label>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 sm:gap-3 mt-3 sm:mt-4">
                                <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-none border-white/20 text-white text-xs sm:text-sm px-3 sm:px-4">Отмена</Button>
                                <Button onClick={handleSubmit} disabled={submitting || !form.author_name.trim() || !form.text.trim()} className="bg-[#0099FF] text-white rounded-none text-xs sm:text-sm px-3 sm:px-4" data-testid="review-submit-btn">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                                    Отправить
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
        </>
    );
}
